import { siteInfo, socialProfiles } from "./site-info";

export const SITE_URL = siteInfo.url;

const ORGANIZATION_ID = `${SITE_URL}/#organization`;

/** Builds a TanStack head() script entry for JSON-LD structured data. */
export function jsonLdScript(data: Record<string, unknown>) {
  return { type: "application/ld+json", children: JSON.stringify(data) };
}

export function breadcrumbLd(items: { name: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}

export function faqLd(faqs: { question: string; answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };
}

export const organizationLd = {
  "@context": "https://schema.org",
  "@type": "OnlineBusiness",
  "@id": ORGANIZATION_ID,
  name: siteInfo.name,
  legalName: siteInfo.legalName,
  url: SITE_URL,
  email: siteInfo.email,
  logo: `${SITE_URL}${siteInfo.logoPath}`,
  address: { "@type": "PostalAddress", ...siteInfo.address },
  sameAs: socialProfiles.map((profile) => profile.href),
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: siteInfo.email,
    url: `${SITE_URL}/impressum#kontakt`,
  },
  areaServed: { "@type": "Country", name: "Schweiz" },
  description:
    "Begner verbindet Menschen in der Schweiz mit Handwerkern, Bauunternehmen und Immobilienprofis.",
};

export const websiteLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: siteInfo.name,
  url: SITE_URL,
  publisher: { "@id": ORGANIZATION_ID },
  inLanguage: "de-CH",
};

const GERMAN_MONTHS = [
  "januar",
  "februar",
  "märz",
  "april",
  "mai",
  "juni",
  "juli",
  "august",
  "september",
  "oktober",
  "november",
  "dezember",
];

/** Converts "12. Juni 2026" to "2026-06-12"; returns undefined if unparseable. */
export function germanDateToIso(value: string): string | undefined {
  const match = value.trim().match(/^(\d{1,2})\.\s*([A-Za-zäöüÄÖÜ]+)\s+(\d{4})$/);
  if (!match) return undefined;
  const month = GERMAN_MONTHS.indexOf(match[2].toLowerCase());
  if (month < 0) return undefined;
  return `${match[3]}-${String(month + 1).padStart(2, "0")}-${match[1].padStart(2, "0")}`;
}

/** Parses "CHF 3'500 bis 7'000" into numeric bounds. */
export function parseChfRange(range: string): { min: number; max: number } | undefined {
  const numbers = range.replace(/'/g, "").match(/\d+(?:\.\d+)?/g);
  if (!numbers || numbers.length === 0) return undefined;
  const min = Number(numbers[0]);
  const max = Number(numbers[numbers.length - 1]);
  return { min, max };
}

/** Schema.org Offer entries with CHF price ranges for a list of guide prices. */
export function priceOffersLd(prices: { label: string; range: string }[]) {
  return prices.flatMap((price) => {
    const bounds = parseChfRange(price.range);
    if (!bounds) return [];
    return [
      {
        "@type": "Offer",
        name: price.label,
        priceCurrency: "CHF",
        priceSpecification: {
          "@type": "PriceSpecification",
          priceCurrency: "CHF",
          minPrice: bounds.min,
          maxPrice: bounds.max,
          valueAddedTaxIncluded: true,
        },
      },
    ];
  });
}

export function geoMeta(lat: number, lng: number, region: string, placename: string) {
  return [
    { name: "geo.region", content: region },
    { name: "geo.placename", content: placename },
    { name: "geo.position", content: `${lat};${lng}` },
    { name: "ICBM", content: `${lat}, ${lng}` },
  ];
}
